// Team reporting-line data layer (Team & Admin §1 — /hub/admin/team).
//
// Modelled on api/hub/now.js: throw-on-error, unwrap the `{error:true, message}`
// gate envelope, label errors by RPC name, one jittered retry on a transport (not
// database) failure for READS only. Writes are never retried — a transport failure
// surfaces a real error and the admin re-attempts consciously.
//
// VERIFIED (2026-09-14 — Team & Admin backend answers):
//   fn_team_load(p_member_id uuid?)        → jsonb  // no arg = resolve the caller; { team[], totals }
//   fn_team_set_manager(p_member_id uuid!, p_manager_id uuid?, p_is_primary boolean!)
//                                          // null manager + is_primary:true = top of tree
//   fn_team_remove_manager(p_member_id uuid!, p_manager_id uuid!)
//
// A person can have MORE THAN ONE manager. is_primary = true is the org-chart line
// (the one to draw in a tree); additional managers are is_primary = false. The
// functions refuse cycles and self-management server-side, so the UI does NOT
// check for either — it surfaces the refusal message verbatim (knowledge-base §5).
//
// HUB_STUB is a local-dev-only fixtures override (REACT_APP_HUB_STUB=1).

import { supabase } from "../../lib/supabase";

const HUB_STUB =
  typeof process !== "undefined" &&
  !!process.env &&
  process.env.REACT_APP_HUB_STUB === "1";

// A refused HTTP/2 stream is a transport failure, not a database one — retry once.
// A Postgres error / RLS refusal / bad parameter carries a `code` and fails on the
// first attempt, where it belongs. (Mirrors api/hub/now.js.)
const TRANSPORT_FAILURE =
  /failed to fetch|networkerror|load failed|err_http2|connection closed/i;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || "");

// Shared RPC call. Writes pass `retry: false` — only reads retry on a transport
// failure. The gate envelope ({error:true, message}) is unwrapped and thrown as
// the raw message so the page can surface it verbatim (knowledge-base §5).
async function teamRpc(name, params, label, { retry = true } = {}) {
  if (HUB_STUB) return teamStub(name, params);
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  if (retry && isTransportFailure(result.error)) {
    await sleep(200 + Math.floor(Math.random() * 250));
    result = await attempt();
  }
  const { data, error } = result;
  if (error) {
    const detail = error?.message || "Supabase RPC failed";
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  if (data && typeof data === "object" && data.error === true) {
    throw new Error(data.message || `${name} returned an error`);
  }
  return data;
}

// ── Read: the reporting-line tree ──────────────────────────────────────────────
// fn_team_load(p_member_id uuid?) → { team: [...], totals: {...} }
// No arg = resolve the caller (auth.uid()); pass a member id to view beneath
// someone. The team array is the people beneath the caller.
export async function getTeamLoad(memberId = null) {
  const params = {};
  if (memberId) params.p_member_id = memberId;
  return teamRpc("fn_team_load", params, "team load");
}

// ── Write: set a manager (primary or additional) ──────────────────────────────
// p_manager_id: null + p_is_primary: true = "top of the tree" (allowed).
// p_is_primary: true = the org-chart line; false = an additional manager.
// Never retried — a write is a deliberate action.
export async function setTeamManager({ memberId, managerId, isPrimary }) {
  return teamRpc(
    "fn_team_set_manager",
    { p_member_id: memberId, p_manager_id: managerId ?? null, p_is_primary: !!isPrimary },
    "team set manager",
    { retry: false },
  );
}

// ── Write: remove a manager ───────────────────────────────────────────────────
// Never retried.
export async function removeTeamManager({ memberId, managerId }) {
  return teamRpc(
    "fn_team_remove_manager",
    { p_member_id: memberId, p_manager_id: managerId },
    "team remove manager",
    { retry: false },
  );
}

// ── Stub (local dev only — HUB_STUB=1) ────────────────────────────────────────
// The confirmed reporting line (Team & Admin backend answers, 2026-09-14):
//   Brian (reports_to_id = null)
//   ├─ Aly Samonte        (reports_to Brian)
//   │  ├─ Ann Sotto       (reports_to Aly)
//   │  └─ Olivia Sayson   (reports_to Aly)
//   └─ Tomoni Majarucon   (reports_to Brian)
//      ├─ Sheane Tolentino
//      ├─ Chariss Vergara
//      ├─ Emman Arcenal
//      └─ Charles Carlo Sio
const STUB_TEAM = {
  team: [
    { id: "u-brian", name: "Brian Park", reports_to_id: null },
    { id: "u-aly", name: "Aly Samonte", reports_to_id: "u-brian" },
    { id: "u-tomoni", name: "Tomoni Majarucon", reports_to_id: "u-brian" },
    { id: "u-ann", name: "Ann Sotto", reports_to_id: "u-aly" },
    { id: "u-olivia", name: "Olivia Sayson", reports_to_id: "u-aly" },
    { id: "u-sheane", name: "Sheane Tolentino", reports_to_id: "u-tomoni" },
    { id: "u-chariss", name: "Chariss Vergara", reports_to_id: "u-tomoni" },
    { id: "u-emman", name: "Emman Arcenal", reports_to_id: "u-tomoni" },
    { id: "u-charles", name: "Charles Carlo Sio", reports_to_id: "u-tomoni" },
  ],
  totals: {},
};

function teamStub(name, params) {
  if (name === "fn_team_load") return STUB_TEAM;
  if (name === "fn_team_set_manager" || name === "fn_team_remove_manager") {
    return { ok: true };
  }
  return null;
}

